import { DistributionPriceLearnMore } from '@/components/pricing/DistributionPriceLearnMore'
import { useStorefrontSiteConfig } from '@/contexts/site-config'
import { darkenHexColor, hexToRgba } from '@/lib/storefront-theme'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

type DistributionBenefitPreviewVariant = 'default' | 'compact' | 'mini'

type DistributionBenefitPreviewProps = {
  label: string
  price: string
  potentialPrice?: string | null
  variant?: DistributionBenefitPreviewVariant
  align?: 'left' | 'right'
  className?: string
  priceClassName?: string
}

const VARIANT_STYLES: Record<
  DistributionBenefitPreviewVariant,
  {
    container: string
    label: string
    price: string
    message: string
    messageWrap: string
  }
> = {
  default: {
    container: 'rounded-lg px-2.5 py-2',
    label: 'text-[10px] tracking-wide',
    price: 'text-[1.6rem] tabular-nums',
    message: 'text-[10px]',
    messageWrap: 'mt-2 min-h-[2.3rem]',
  },
  compact: {
    container: 'rounded-xl px-3 py-3',
    label: 'text-[10px] tracking-[0.14em]',
    price: 'text-lg tabular-nums',
    message: 'text-[11px]',
    messageWrap: 'mt-2',
  },
  mini: {
    container: 'rounded-md px-2 py-2',
    label: 'text-[8px] tracking-[0.12em]',
    price: 'text-sm tabular-nums',
    message: 'text-[9px]',
    messageWrap: 'mt-2',
  },
}

export function DistributionBenefitPreview({
  label,
  price,
  potentialPrice,
  variant = 'default',
  align = 'left',
  className,
  priceClassName,
}: DistributionBenefitPreviewProps) {
  const styles = VARIANT_STYLES[variant]
  const { themeColors } = useStorefrontSiteConfig()
  const { t, i18n } = useTranslation()
  const containerStyle = {
    borderColor: hexToRgba(themeColors.priceText, 0.18),
    backgroundColor: hexToRgba(themeColors.priceCard, 0.74),
  }
  const textStyle = { color: darkenHexColor(themeColors.priceText, 0.08) }
  const priceStyle = { color: themeColors.priceText }

  return (
    <div
      className={cn(
        'border',
        styles.container,
        className
      )}
      style={containerStyle}
    >
      <div className={align === 'right' ? 'text-right' : 'text-left'}>
        <p className={cn('font-semibold uppercase', styles.label)} style={textStyle}>
          {label}
        </p>
        <p className={cn('mt-0.5 font-price font-bold leading-none whitespace-nowrap', styles.price, priceClassName)} style={priceStyle}>
          {price}
        </p>
        <div className={styles.messageWrap}>
          {potentialPrice ? (
            <p className={cn('font-medium leading-snug', styles.message)} style={textStyle}>
              {t('product.distribution_lost_benefit_hint', { value: potentialPrice })}
              {', '}
              <DistributionPriceLearnMore
                label={t('product.distribution_learn_more').toLocaleLowerCase(i18n.resolvedLanguage || 'pt-BR')}
                triggerClassName="inline h-auto px-0 py-0 align-baseline text-current hover:text-current"
              />
            </p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
